import mixpanel from 'mixpanel-browser';
import get from 'lodash/get';
import uaParser from 'ua-parser-js';
import axios from 'axios';
import { splitSizes } from './search_helpers';
import timerQueue, { EventType } from './TimerQueue';
import { isValidEmail } from './validation';

export const PEOPLE_SET = 'set';
export const PEOPLE_SET_ONCE = 'set_once';
export const PEOPLE_APPEND = 'append';
export const PEOPLE_INCREMENT = 'increment';
export const PEOPLE_UNION = 'union';

function initTracking(key) {
  mixpanel.init(key);
}

function searchEngine(referrer) {
  if (referrer.search('https?://(.*)google.([^/?]*)') === 0) {
    return 'google';
  }
  if (referrer.search('https?://(.*)bing.com') === 0) {
    return 'bing';
  }
  if (referrer.search('https?://(.*)yahoo.com') === 0) {
    return 'yahoo';
  }
  if (referrer.search('https?://(.*)duckduckgo.com') === 0) {
    return 'duckduckgo';
  }
  return null;
}

export function trackPeople(
  people_prop_method,
  property_to_update,
  { property_float_value, property_string_value, property_array_value },
) {
  if (!isValidEmail(mixpanel.get_distinct_id())) {
    return;
  }

  track(null, {
    user: {
      property_to_update,
      people_prop_method,
      property_float_value,
      property_string_value,
      property_array_value,
    },
  });
}

export const getClientInfo = () => {
  const queryParamsStr = window.location.search;
  const urlParams = new URLSearchParams(queryParamsStr);
  const referrer = document.referrer || '';
  const parsedUa = uaParser(window.navigator.userAgent);
  return {
    distinct_id: mixpanel.get_distinct_id(),
    current_url: window.location.href,
    referrer,
    referring_domain: referrer ? new URL(referrer).hostname : '',
    initial_referring_domain: mixpanel.get_property('$initial_referring_domain'), // mixpanel specific
    initial_referrer: mixpanel.get_property('$initial_referrer'), // mixpanel specific
    utm_source: urlParams.get('utm_source'),
    utm_content: urlParams.get('utm_content'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    search_engine: searchEngine(referrer),
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    os: `${parsedUa.os.name} ${parsedUa.os.version}`,
    browser: parsedUa.browser.name,
    browser_version: parsedUa.browser.major,
    device: parsedUa.device.model,
  };
};

function track(event, { product, categoryDepartment, user, cart, ui } = {}) {
  setTimeout(() => {
    axios.post('/api/tracking', {
      commons: getClientInfo(),
      event,
      product,
      category_department: categoryDepartment,
      user,
      cart,
      ui,
    });
  }, 0);
}

function getProductTrackingData(
  product,
  currencySymbol,
  { filterSettings, sortType, pdpPage, productVersionOptionId },
) {
  let categories = [];
  let department;
  let category;
  let subcategory;
  if (product.categories) {
    categories = product.categories.split(',');
    if (categories.length === 2) {
      [category, department] = categories;
    } else {
      [subcategory, category, department] = categories;
    }
  } else {
    department = get(product, 'navigation_path[0].name', '');
    category = get(product, 'navigation_path[1].name', '');
    subcategory = get(product, 'navigation_path[2].name', '');
  }
  const onSale = product.reduction_percentage !== 0;
  const categoryTrack = { department: department.toLowerCase(), category, subcategory };
  const productTrack = {
    product_id: product.product_id,
    product_version_id: product.product_version_id,
    brand: product.brand,
    title: product.model,
    season: product.season,
    on_sale: onSale,
    displayed_original_price: onSale ? product.rrp_not_reduced : product.rrp,
    displayed_discount_price: onSale ? product.rrp : null,
    local_currency: currencySymbol,
    store: product.store,
  };
  if (filterSettings) {
    productTrack.filters_applied_seasons = filterSettings.seasons;
    productTrack.filters_applied_designers = filterSettings.refinedBrandName;
    productTrack.filters_applied_sizes = splitSizes(filterSettings.sizes);
  }
  if (sortType) {
    productTrack.sort_type = sortType;
  }
  if (product.images) {
    productTrack.image_count = product.images.length;
  }
  if (product.converted_rrp) {
    productTrack.approx_price = `${product.converted_rrp} USD`;
  }

  if (product.product_version_option_id) {
    productTrack.product_version_option_id = product.product_version_option_id;
  }

  if (productVersionOptionId && product.options) {
    const sizeObject = product.options.find(el => el.product_version_option_id === productVersionOptionId);
    productTrack.product_version_option_id = productVersionOptionId;
    if (sizeObject) {
      productTrack.size = product.size_unit_system_acronym
        ? `${sizeObject.size} ${product.size_unit_system_acronym}`
        : sizeObject.size;
    }
  }

  return {
    product: productTrack,
    categoryDepartment: categoryTrack,
  };
}

function getProductsViewedData(categoryPath, categoryName, { query, isBrand, brandName }) {
  let type = 'Category';
  let value = categoryName;
  if (query) {
    type = 'Search';
    value = query;
  } else if (isBrand) {
    type = 'Designer';
    value = brandName;
  }

  let department = '';
  let category = '';
  let subcategory = '';
  if (categoryPath) {
    department = categoryPath[0] ? categoryPath[0].name.toLowerCase() : '';
    category = categoryPath[1] ? categoryPath[1].name : '';
    subcategory = categoryPath[2] ? categoryPath[2].name : '';
  }
  return {
    ui: {
      type,
      value,
    },
    categoryDepartment: { department, category, subcategory },
  };
}

export { getProductsViewedData, getProductTrackingData, initTracking, track };
